import React from 'react';
import { Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CalendarPicker from '../../CalendarPicker';

/**
 * DateSelectionSection Component
 *
 * Date selection interface with support for single or recurring bookings.
 * Shows "Book all available dates" checkbox for recurring bookings.
 * Displays calendar picker for date selection.
 *
 * State Ownership:
 * - Reads all props from parent (no local state)
 * - Date state managed by parent component
 *
 * @param {Object} props
 * @param {boolean} props.canBookRecurring - Whether recurring bookings are allowed
 * @param {boolean} props.bookAllAvailableDates - Whether "book all dates" is checked
 * @param {string[]} props.selectedDates - Array of selected ISO date strings (YYYY-MM-DD) for recurring
 * @param {Date|null} props.selectedDate - Single selected date (for non-recurring)
 * @param {string[]} props.availableDates - Array of available ISO date strings (YYYY-MM-DD)
 * @param {number} props.bookingWindowDays - Number of days in booking window
 * @param {Function} props.onBookAllChange - Handler for "book all dates" checkbox
 * @param {Function} props.onDateSelect - Handler for date selection
 */
const DateSelectionSection = ({
  canBookRecurring,
  bookAllAvailableDates,
  selectedDates,
  selectedDate,
  availableDates,
  bookingWindowDays,
  onBookAllChange,
  onDateSelect
}) => {
  const { t } = useTranslation();

  return (
    <div>
      {canBookRecurring ? (
        <>
          {/* Book All Available Dates Checkbox */}
          <div className="mb-4">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={bookAllAvailableDates}
                onChange={(e) => onBookAllChange(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="ml-2 text-sm font-medium">
                {t('bookForAllAvailableDates')}
              </span>
            </label>
          </div>

          {/* Calendar Picker for Recurring Bookings */}
          {!bookAllAvailableDates && (
            <>
              <label className="block text-sm font-medium mb-1">
                <Calendar className="w-5 h-4 inline-block mr-1 text-blue-500" />
                {t('orSelectDates')}
              </label>
              <CalendarPicker
                canBookRecurring={true}
                selectedDates={selectedDates}
                onDateSelect={onDateSelect}
                availableDates={availableDates}
                bookingWindowDays={bookingWindowDays}
                className="w-full"
              />
            </>
          )}
        </>
      ) : (
        <>
          {/* Calendar Picker for Single Bookings */}
          <label className="block text-sm font-medium mb-1">
            <Calendar className="w-5 h-4 inline-block mr-1 text-blue-500" />
            {t('selectDateNextDays', { days: bookingWindowDays })}
          </label>
          <CalendarPicker
            selectedDate={selectedDate}
            canBookRecurring={false}
            onDateSelect={onDateSelect}
            availableDates={availableDates}
            bookingWindowDays={bookingWindowDays}
            className="w-full"
          />
        </>
      )}

      {/* No Available Dates Warning */}
      {availableDates.length === 0 && (
        <p className="text-yellow-600 text-sm mt-1">
          {t('noAvailableDatesForThisRoute')}
        </p>
      )}
    </div>
  );
};


export default React.memo(DateSelectionSection);
